import { z } from 'zod';
import { macroSetSchema, microSetSchema } from './food.js';
import { bodyMetricKindSchema, clarificationSchema, confidenceBandSchema, entryStatusSchema, logIntentSchema, metricKeySchema, resolvedBySchema, validationNoteSchema, } from './pipeline.js';
/* ==========================================================================
 *  Logging HTTP wire contract (ISSUE-020 / ISSUE-024 / ISSUE-026)
 *
 *  This is the seam where the (server-only) extraction pipeline meets the web
 *  app. The pipeline emits DRAFTS (`MealEntryDraft`, no identity); the chip UI
 *  needs PERSISTED entries it can edit, delete, and reconcile by id. These are
 *  the *view* + *request* shapes that cross the network — distinct from the
 *  internal draft shapes in `pipeline.ts`.
 *
 *  Invariants carried into the contract (ADR-0003):
 *  - The client never sends macros. Edits send quantity/unit/food; the server
 *    recomputes macros deterministically (`scaleMacros`, FR-5.4).
 *  - Every view object carries a server-authoritative id, so optimistic UI can
 *    reconcile against the response.
 *  - Mutating responses (log / edit / delete / restore) echo the affected
 *    `DaySummary` so the totals bar updates without a second round-trip.
 *  - Clarifications are returned, not awaited; answering one is a fresh call
 *    (the pipeline stays stateless — ADR-0002 decision 7).
 * ========================================================================== */
/* ----- Persisted entry views (what chips render) ------------------------- */
/** A persisted meal entry as the web app sees it. Numbers are numbers here; the
 *  route layer converts the DB's numeric/string columns. `deletedAt` non-null
 *  means soft-deleted (kept for the 5s undo window + audit). */
export const mealEntryViewSchema = z.object({
    id: z.string().uuid(),
    foodId: z.string().nullable(), // canonical `${source}:${sourceId}`; null if unresolved
    name: z.string(),
    /** Human-facing portion ("2", "slices"); `quantityG` is the normalized basis. */
    displayQuantity: z.number().positive().nullable(),
    displayUnit: z.string().nullable(),
    quantityG: z.number().nonnegative(),
    macros: macroSetSchema, // absolute totals for the portion
    confidence: z.number().min(0).max(1),
    // NOTE: keep these as bare enums — this schema is registered with zod-to-openapi,
    // which cannot convert `.catch()`/wrapper types. Tolerance for out-of-enum DB
    // values lives in the route serializers via `coerceEntryStatus`/`coerceResolvedBy`
    // (NUTIANS-API-C / -B).
    band: confidenceBandSchema, // drives the confidence dot (green/amber/red)
    status: entryStatusSchema, // ready | needs_clarification | blocked
    validationNotes: z.array(validationNoteSchema).default([]),
    recordedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable().default(null),
    /** Per-entry resolution provenance (ADR-0013 §5.3). Null for legacy rows. */
    resolvedBy: resolvedBySchema.nullable().optional(),
});
export const bodyMetricViewSchema = z.object({
    id: z.string().uuid(),
    metric: bodyMetricKindSchema,
    value: z.number(),
    unit: z.string(),
    recordedAt: z.string().datetime(),
});
/* ----- Day summary (shared with ISSUE-026 ledger) ------------------------ */
/** Target vs consumed for one day. Deterministic sum of entries (FR-5.1/5.4) —
 *  no AI. Embedded in every mutating log response AND returned standalone by
 *  the ledger endpoints (ISSUE-026). `remaining` = target − consumed (may be
 *  negative = over). `target` is null before the user has an effective target.
 *  `overridden` is true when ≥1 manual_metric_override row has been applied
 *  (ADR-0013 §7.3 — drives the "Manual override applied" badge). */
export const daySummarySchema = z.object({
    date: z.string().date(),
    target: z
        .object({
        kcal: z.number().int().nonnegative(),
        proteinG: z.number().int().nonnegative(),
        carbsG: z.number().int().nonnegative(),
        fatG: z.number().int().nonnegative(),
        fiberG: z.number().int().nonnegative(),
    })
        .nullable(),
    consumed: macroSetSchema,
    remaining: z
        .object({
        kcal: z.number(),
        proteinG: z.number(),
        carbsG: z.number(),
        fatG: z.number(),
        fiberG: z.number(),
    })
        .nullable(), // null when no target
    entryCount: z.number().int().nonnegative(),
    /** True when at least one manual_metric_override was applied to this day. */
    overridden: z.boolean().default(false),
    /**
     * Aggregated micronutrient totals for the day (T13.2 / ISSUE-076).
     * Null when there are no resolved entries. Per-field null = no upstream data
     * for any entry on that day — display as "—", never as 0. Additive only.
     */
    micros: microSetSchema.nullable().default(null),
});
/* ----- ADR-0013 §7: manual metric override view -------------------------- */
/** Minimal view of a manual_metric_override row (Phase B adds the full handler).
 *  Embedded in logResponseSchema so clients can display the override badge. */
export const manualMetricOverrideViewSchema = z.object({
    id: z.string().uuid(),
    date: z.string().date(),
    metric: metricKeySchema,
    value: z.number(),
});
/* ----- POST /me/logs (ISSUE-020) ----------------------------------------- */
/**
 * Image quota state returned in the vision log response (ADR-0014 §4 / T6.1).
 * Mirrors the chat done-event pattern so mobile can show remaining captures.
 */
export const imageQuotaSchema = z.object({
    used: z.number().int().nonnegative(),
    limit: z.number().int().positive().nullable(), // null = unlimited (premium with no hard cap)
    resetsAt: z.string().datetime(),
});
/**
 * Shared image-capture fields (ADR-0014 §3). Reused by the natural-language log
 * endpoint (`createLogSchema`) and the estimate-only endpoint
 * (`visionEstimateRequestSchema`) so the payload constraints stay in one place.
 */
export const logImageFieldsSchema = z.object({
    /**
     * Base64-encoded image payload (ADR-0014 §3 / V-1 photo capture). No `data:` prefix.
     * When present: extraction-vision pipeline replaces the text extraction stage.
     * Image bytes are processed in-memory and never persisted (process-then-discard).
     * Max decoded size ~1.5 MB; client should compress to ≤0.5 JPEG quality before encoding.
     */
    imageBase64: z.string().min(1).optional(),
    /** MIME type of the image. Required when imageBase64 is set. */
    imageMediaType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/gif']).optional(),
    /**
     * Capture intent for the image (ADR-0014 §V-2). Defaults to 'meal' (food-photo path).
     * 'label' routes to the nutrition-label extraction prompt, which returns statedMacros
     * entities that bypass food-DB resolution (→ resolvedBy='user_stated').
     * Both modes share the same image_quota_daily budget.
     */
    imageCapture: z.enum(['meal', 'label']).optional(),
});
/** Body for the natural-language log endpoint. `clientNow` + `timezone` let the
 *  server resolve "this morning"/"yesterday" relative to the user's clock
 *  without trusting it for storage (server stamps the canonical time). */
export const createLogSchema = logImageFieldsSchema
    .extend({
    text: z.string().max(2000).default(''),
    clientNow: z.string().datetime().optional(),
    timezone: z.string().max(64).optional(), // IANA, e.g. "Europe/Lisbon"
    /**
     * Client-generated idempotency key (ADR-0003 §Mobile M6). Additive + optional:
     * web omits it (unchanged behaviour). The mobile durable offline queue sends its
     * local row id so an at-least-once replay (POST landed, response lost) returns the
     * EXISTING LogResponse instead of double-logging the meal (protects FR-5.4). The
     * server enforces uniqueness on (userId, clientLogId) at the DB level.
     */
    clientLogId: z.string().uuid().optional(),
})
    .refine((v) => !(v.imageBase64 !== undefined && v.imageMediaType === undefined), {
    message: 'imageMediaType is required when imageBase64 is provided.',
})
    .refine((v) => v.imageBase64 !== undefined || v.text.length >= 1, {
    message: 'text must be at least 1 character when no image is provided.',
});
/* ----- POST /me/vision/estimate (T35.1 — estimate-only, no persistence) ---- */
/**
 * Body for the estimate-only vision endpoint (ADR-0014 estimate mode). Same image
 * constraints as `createLogSchema`, but an image is always required (there is no
 * text fallback — this surface is "what's on this plate?").
 */
export const visionEstimateRequestSchema = logImageFieldsSchema.required({
    imageBase64: true,
    imageMediaType: true,
});
/**
 * One estimated item — a subset of `mealEntryViewSchema` without the persisted
 * identity fields (no id/recordedAt: nothing is stored). Carries enough to render
 * a row and recompute totals when the user excludes an item before "I ate this".
 */
export const visionEstimateItemSchema = z.object({
    name: z.string(),
    displayQuantity: z.number().positive().nullable().default(null),
    displayUnit: z.string().nullable().default(null),
    quantityG: z.number().nonnegative(),
    macros: macroSetSchema,
    resolvedBy: resolvedBySchema.nullable().optional(),
});
/**
 * Response from `POST /me/vision/estimate`. Mirrors the vision-log pipeline output
 * minus persistence: no rawLogId, no DaySummary. `totals` is the summed macro set
 * of `items`; `imageQuota` mirrors the log-path envelope so the mobile upsell gate
 * and remaining-captures indicator work identically.
 */
export const visionEstimateResponseSchema = z.object({
    intent: logIntentSchema,
    items: z.array(visionEstimateItemSchema).default([]),
    totals: macroSetSchema,
    notes: z.array(z.string()).default([]),
    imageQuota: imageQuotaSchema.nullable().default(null),
});
/** Response from `POST /me/logs` (and from answering a clarification). Carries
 *  the persisted entries, any unresolved clarifications, and the live day
 *  totals so the chips + bar render from one payload (J2).
 *
 *  ADR-0013 Phase A adds two optional backward-compatible fields:
 *  - `redirect`: set for `question` intent — client should open that URL.
 *  - `metricOverride`: set when a manual override was applied (Phase B).
 *  ADR-0014 T6.1:
 *  - `imageQuota`: present when the request included an image; mirrors the chat
 *    done-event pattern so mobile can show remaining captures. */
export const logResponseSchema = z.object({
    rawLogId: z.string().uuid(),
    intent: logIntentSchema,
    entries: z.array(mealEntryViewSchema).default([]),
    bodyMetrics: z.array(bodyMetricViewSchema).default([]),
    notes: z.array(z.string()).default([]),
    clarifications: z.array(clarificationSchema).default([]),
    day: daySummarySchema,
    /** Clients that see this set should navigate to the given URL instead of rendering entries. */
    redirect: z.string().optional(),
    /** Non-null when a manual override was applied to today's summary (Phase B). */
    metricOverride: manualMetricOverrideViewSchema.nullable().default(null),
    /** Present when the request used the vision path (imageBase64 was sent). */
    imageQuota: imageQuotaSchema.nullable().default(null),
});
/* ----- POST /me/logs/:rawLogId/clarify (FR-3.3) -------------------------- */
/**
 * Answer one inline clarification. Three shapes, by how the user resolved it:
 *  - `chosenFoodId` set → the user picked a candidate; the server skips
 *    re-extraction and runs resolve→macros→persist deterministically (cheap, no
 *    AI call).
 *  - `answerText` set → free-text ("cooked"); the server re-runs extraction for
 *    that entity with the answer folded in.
 *  - `decline: true` → "I didn't have this"; removes the clarification from
 *    metadata without creating an entry (no AI call, no persist).
 * Exactly one of the three must be present. Returns a fresh `LogResponse`.
 */
export const clarifyLogSchema = z
    .object({
    entityName: z.string().min(1),
    chosenFoodId: z.string().nullable().default(null),
    answerText: z.string().max(500).nullable().default(null),
    decline: z.boolean().optional().default(false),
})
    .refine((v) => {
    const choices = [v.chosenFoodId !== null, v.answerText !== null, v.decline === true];
    return choices.filter(Boolean).length === 1;
}, { message: 'Provide exactly one of chosenFoodId, answerText, or decline.' });
/* ----- PATCH /me/entries/:id (FR-4.1 edit-in-place) ---------------------- */
/**
 * Inline edit of a persisted entry. The client sends only what changed; the
 * server recomputes macros from quantity × the food's per-100 basis via
 * `scaleMacros` (NEVER trusts client-supplied macros — FR-5.4).
 * `saveAsOverride` opts the food correction into the per-user override layer
 * for future extractions (FR-4.2 / ISSUE-025). At least one field required.
 *
 * ADR-0013 §8.3: `directMacros` lets the user supply macros directly for
 * entries that have no food record (`foodId` is null). Server-side guard
 * enforces `foodId IS NULL` on the target entry. `directMacros` and `foodId`
 * are mutually exclusive (schema refine + route guard).
 */
export const updateEntrySchema = z
    .object({
    displayQuantity: z.number().positive().nullable().optional(),
    displayUnit: z.string().max(64).nullable().optional(),
    foodId: z.string().optional(), // re-point to a different canonical food
    saveAsOverride: z.boolean().optional(),
    /** Rename the entry's display name (e.g. correcting a vision guess —
     *  "pineapple" → "peach"). Without `reestimate`, macros are NOT recomputed:
     *  the numbers stay whatever the entry already carries. */
    name: z.string().trim().min(1).max(200).optional(),
    /** With `name`: re-resolve the corrected name (food DB first, tier-model
     *  AI estimate otherwise) at the entry's EXISTING portion — same grams,
     *  new food's numbers. Ignored without `name`; invalid with directMacros. */
    reestimate: z.boolean().optional(),
    /** Re-stamp when the entry happened — used to move an entry to a
     *  different meal slot (slot labels derive from recordedAt everywhere). */
    recordedAt: z.string().datetime().optional(),
    /** Direct macro override for entries with no catalogue food (foodId = null).
     *  Cannot be combined with foodId — they are mutually exclusive. */
    directMacros: z
        .object({
        kcal: z.number().min(0),
        proteinG: z.number().min(0),
        carbsG: z.number().min(0),
        fatG: z.number().min(0),
        fiberG: z.number().min(0),
    })
        .optional(),
})
    .strict()
    .refine((v) => v.displayQuantity !== undefined ||
    v.displayUnit !== undefined ||
    v.foodId !== undefined ||
    v.directMacros !== undefined ||
    v.name !== undefined ||
    v.recordedAt !== undefined, {
    message: 'Provide at least one of displayQuantity, displayUnit, foodId, directMacros, name, or recordedAt.',
})
    .refine((v) => !(v.directMacros !== undefined && v.foodId !== undefined), {
    message: 'directMacros cannot be combined with foodId.',
})
    .refine((v) => !(v.reestimate === true && v.name === undefined), {
    message: 'reestimate requires name.',
})
    .refine((v) => !(v.reestimate === true && v.directMacros !== undefined), {
    message: 'reestimate cannot be combined with directMacros.',
});
/* ----- POST /me/logs/estimate-item (single-item re-estimate) -------------- */
/**
 * Re-price ONE item after the user corrects its name in the photo-estimate
 * sheet ("pineapple" → "peach"): the portion is pinned by the caller and never
 * guessed. Resolution order matches written logs — food DB (user overrides
 * included, usually zero AI) then a tier-model estimate. Persists nothing.
 */
export const estimateItemSchema = z.object({
    name: z.string().trim().min(1).max(200),
    /** Gram-normalised portion the macros must be priced for. 0 = portion
     *  unknown → the estimate prices one typical serving. */
    quantityG: z.number().nonnegative().max(100000),
    displayQuantity: z.number().positive().nullable().optional(),
    displayUnit: z.string().max(64).nullable().optional(),
});
export const estimateItemResponseSchema = z.object({
    name: z.string(),
    quantityG: z.number(),
    displayQuantity: z.number().nullable(),
    displayUnit: z.string().nullable(),
    macros: macroSetSchema,
    /** 'user_override' | 'open_food_facts' | 'usda' | 'ai_haiku' | 'ai_sonnet',
     *  or 'unresolved' when both the DB and the AI estimate came up empty —
     *  the caller should keep its previous numbers in that case. */
    resolvedBy: resolvedBySchema.nullable(),
});
/* ----- POST /me/entries (barcode / manual create path) -------------------- */
/**
 * Create a single meal entry directly, bypassing the NLP pipeline. Two variants:
 *  (a) `foodId` + optional `displayQuantity`/`displayUnit` — server scales macros
 *      from the canonical food's per-100 basis (same arithmetic as PATCH).
 *  (b) `directMacros` + optional `name` — caller-supplied absolute macros written
 *      as-is; resolvedBy = 'user_direct'. Used for barcode not-found fallback.
 * Exactly one of foodId / directMacros must be present.
 */
export const createEntrySchema = z
    .object({
    name: z.string().min(1).max(500).optional(),
    foodId: z.string().optional(),
    displayQuantity: z.number().positive().optional(),
    displayUnit: z.string().max(64).optional(),
    directMacros: z
        .object({
        kcal: z.number().min(0),
        proteinG: z.number().min(0),
        carbsG: z.number().min(0),
        fatG: z.number().min(0),
        fiberG: z.number().min(0),
    })
        .optional(),
    recordedAt: z.string().datetime({ offset: true }).optional(),
})
    .refine((b) => b.foodId !== undefined || b.directMacros !== undefined, {
    message: 'Provide either foodId or directMacros.',
})
    .refine((b) => !(b.foodId !== undefined && b.directMacros !== undefined), {
    message: 'foodId and directMacros are mutually exclusive.',
});
/** Response from edit / delete / restore: the affected entry (or null if it was
 *  hard-gone) plus the recomputed day totals for optimistic reconciliation. */
export const entryMutationResponseSchema = z.object({
    entry: mealEntryViewSchema.nullable(),
    day: daySummarySchema,
});
/* ----- GET /me/days/:date/entries (ADR-0013 §10.1 Phase E) --------------- */
/** One source log and its non-deleted meal entries for the day. Grouped by
 *  source_log_id so the UI can show entries under the original log text. */
export const entryListGroupSchema = z.object({
    rawLogId: z.string().uuid(),
    /** When the source log was submitted (raw_log.submitted_at). */
    submittedAt: z.string().datetime(),
    /** The classified intent of the source log (raw_log.extraction_metadata.intent). */
    intent: logIntentSchema,
    /** Decrypted original log text (ADR-0025). Null for synthetic/placeholder logs
     *  (barcode scans, manual entries, health imports — texts that start with `[`). */
    text: z.string().nullable(),
    entries: z.array(mealEntryViewSchema),
});
/** Response from GET /me/days/:date/entries (ADR-0013 §10.1).
 *  Non-deleted meal_entry rows for the local date, grouped by source_log_id.
 *  UTC day-window is identical to computeDaySummary so totals always reconcile. */
export const entryListResponseSchema = z.object({
    date: z.string().date(),
    groups: z.array(entryListGroupSchema),
});
/* ----- GET /me/days (ISSUE-026) ------------------------------------------ */
/** Query for the rolling-average range view (FR-5.2): 7d + 28d windows. */
export const daysRangeQuerySchema = z.object({
    from: z.string().date().optional(),
    to: z.string().date().optional(),
});
/** Average consumed macros over a rolling window (avg7 / avg28 in the range response). */
export const averageMacrosSchema = z.object({
    kcal: z.number(),
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
    fiberG: z.number(),
});
/** Response from `GET /me/days` (FR-5.2): one DaySummary per calendar date in
 *  [from, to], plus 7-day and 28-day rolling averages of consumed macros.
 *  `avg7` / `avg28` are null only if the computed window is empty (from > to). */
export const daysRangeResponseSchema = z.object({
    days: z.array(daySummarySchema),
    avg7: averageMacrosSchema.nullable(),
    avg28: averageMacrosSchema.nullable(),
});
/** Micros where "below reference" is a safe, neutral thing to name (ADR-0009 §4 /
 *  brand-guide ED-safe lexicon: never an "eat less" nudge). Sugar, saturated fat, and
 *  sodium are ceiling nutrients and are shown in the panel without gap framing. */
export const nutrientGapKeySchema = z.enum([
    'fiberG',
    'potassiumMg',
    'calciumMg',
    'ironMg',
    'magnesiumMg',
    'vitaminDUg',
    'vitaminB12Ug',
]);
/** Premium nutrient panel (T13.3 / ISSUE-076, `nutrient_panel` feature): the rolling
 *  average of all 10 micros over the insights window, plus which floor nutrients are
 *  below their reference intake this window. Free/Lite already sees all 10 per-day via
 *  `DaySummary.micros` — this adds the rolling aggregate + gap analysis only. `average`
 *  fields are null when no resolved entry in the window carried that field.
 *  `windowDays` counts only days with at least one resolved micro value. */
export const nutrientPanelSchema = z.object({
    windowDays: z.number().int().nonnegative(),
    average: microSetSchema,
    belowReference: z.array(nutrientGapKeySchema),
});
/** Response from `GET /me/insights` (ADR-0012 §3, `advanced_insights` feature):
 *  rolling 7-day and 28-day macro averages, plus the premium nutrient panel over the
 *  28-day window. All fields null only when the window is empty. */
export const insightsResponseSchema = z.object({
    rollingAvg7: averageMacrosSchema.nullable(),
    rollingAvg28: averageMacrosSchema.nullable(),
    nutrientPanel: nutrientPanelSchema.nullable(),
});
//# sourceMappingURL=log.js.map